import JWT from "../encoder/jwt";
import LocalStoragePersistence from "../persistence/local-storage";
import AxiosApi from "./axios-api";

/**
 * Reads the stored JWT, tolerating both the JSON-quoted form written at login and a bare token.
 * Returns "" when absent or unreadable, so callers can treat it as "no session".
 */
export function readAuthToken(storage) {
  const raw = storage.getItem("gentell-fastcare-authToken");
  if (!raw) return "";
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === "string" ? parsed : "";
  } catch {
    // Already a bare token (or corrupt) — use it as-is rather than dropping the session.
    return raw;
  }
}

export default class AxiosTokenApi extends AxiosApi {
  /**
   * Storage instance
   * @type {LocalStoragePersistence}
   */
  _storage;

  /**
   * JWT instance
   * @type {JWT}
   */
  _jwt;

  /**
   * AxiosTokenApi constructor
   * @param {LocalStoragePersistence} storage
   * @param {JWT} jwt
   */
  constructor(storage, jwt) {
    super();

    this._storage = storage;
    this._jwt = jwt;

    // The token is written to localStorage with JSON.stringify at login, so the raw value is
    // wrapped in quotes. Reading it back without parsing produced `Bearer "eyJ..."`, which the
    // server rejects with a 401 (verified against the API), so this refresh call could never
    // authenticate and the session was never renewed. api.js and OrderSyncPage both parse; this
    // was the one reader that did not.
    //
    // The request interceptor that used to live here has been removed. AxiosApi hands us the
    // SHARED axiosInstance, so every construction added another interceptor to it, and refresh()
    // builds a new AxiosTokenApi on every run. Those stacked interceptors each closed over the
    // token captured at construction time and overwrote Authorization for every request on the
    // instance, so a stale token could clobber a fresh one. api.js already attaches Authorization
    // to axiosInstance from the same source, so nothing is lost by dropping it.
    const token = readAuthToken(this._storage);
    this._jwt.setToken(token);
  }

  async refreshToken() {
    try {
      const token = this._jwt.getToken();
  
      if (!token) {
        return;
      }
  
      const response = await this.get("account/refreshToken");
      const { result: tokenRefreshed } = response;

      // Only overwrite the stored token when the server actually returned one. Writing an
      // undefined result would store the literal string "undefined" and poison the session,
      // which is worse than leaving the current token in place for another attempt.
      if (typeof tokenRefreshed === "string" && tokenRefreshed) {
        this._storage.setItem("gentell-fastcare-authToken", JSON.stringify(tokenRefreshed));
      }
    } catch (error) {
      // Only a genuine auth failure ends the session. A network blip or timeout must not clear
      // it, or a brief outage would strand every signed-in user — the same mistake Bug 4092
      // fixed for the shared interceptor. Anything else is left for the next attempt.
      if (error?.response?.status === 401) {
        this._storage.removeItem("gentell-fastcare-authToken");
        this._storage.removeItem("gentell-fastcare-authUser");
      } else {
        // eslint-disable-next-line no-console
        console.error("Token refresh failed without a response (network/timeout).", error);
      }
    }
  }
}
